import React, { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { Loading, Modal, Pagination, Stack } from '@therms/atalaya'
import { useUrlState } from '@src/hooks/useUrlState'
import { useHistory, useParams } from 'react-router-dom'
import {
  getTemplateReportAggregationData,
  MultiChoiceQuestion,
} from '@src/generatedClient/generated/taskreportsApi'
import {
  MdOutlineCalendarToday,
} from 'react-icons/md'

type DateRangeFormProps = {
  initStartDate: string
  initEndDate: string
  onConfirm?: (startDate: string, endDate: string) => void
  onCancel?: () => void
}

function DateRangeForm({ initStartDate, initEndDate, onConfirm, onCancel }: DateRangeFormProps) {
  const [startDate, setStartDate] = useState(initStartDate)
  const [endDate, setEndDate] = useState(initEndDate)

  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className="block">
          Start Date
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="block w-full mt-1 border rounded-md p-2"
          />
        </label>
      </div>

      <div className="space-y-2">
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className="block">
          End Date
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="block w-full mt-1 border rounded-md p-2"
            min={startDate} // Prevent selecting end date before start date
          />
        </label>
      </div>

      <div className="flex justify-end space-x-4 pt-4">
        <div className="py-4">
          <button
            type="button"
            className="px-4 py-2 rounded  bg-gray-300"
            onClick={()=>{
              onCancel?.()
            }}
          >
            Cancel
          </button>
        </div>
        <div className="py-4">
          <button
            type="button"
            disabled={!startDate || !endDate}
            className="px-4 py-2 rounded  bg-blue-500 text-white"
            onClick={()=>{
              onConfirm?.(startDate, endDate)
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  )
}

function AggregationReportItem({
  report,
}: {
  report: MultiChoiceQuestion
}) {
  return (
    <div className='w-full h-full bg-gray-100 rounded-xl px-6 py-4'>
      <h3 className="text-sm">{report.question}</h3>
      <p className="text-[18px] font-medium">{report.answer}</p>
    </div>
  )
}

interface ViewAggregationReportProps {
  siteId: string
}

function ViewAggregationReport({ siteId }: ViewAggregationReportProps) {
  const [loading, setLoading] = useState(false)
  const [skippedRecords, setSkippedRecords] = useState(0)
  const [showDateRangeFormModal, setShowDateRangeFormModal] = useState(false)
  const limit = 10

  const history = useHistory()

  const { dateRange } = useParams<{
    dateRange?: string
  }>()

  const [templateReportId] = useUrlState('trId', '')

  const [endDate, setEndDate] = useUrlState('endDate', new Date().toISOString())
  const [startDate, setStartDate] = useUrlState(
    'startDate',
    dateRange
      ? dayjs().subtract(30, 'day').toISOString()
      : dayjs().subtract(1, 'day').toISOString(),
  )

  const getLocalizedDate = (d: string | Date) => {
    if (typeof d !== 'string') return new Date(d)
    const parts = d.split('-')
    return new Date(
      parseInt(parts[0], 10),
      parseInt(parts[1], 10) - 1,
      parseInt(parts[2], 10),
    )
  }

  const [reports, setReports] = useState<MultiChoiceQuestion[]>([])

  const paginatedReports = useMemo(() => {
    const start = (skippedRecords > 0 ? skippedRecords - 1 : 0) * limit
    const end = start + limit
    return reports.slice(start, end)
  }, [reports, skippedRecords])

  const fetchFunction = async () => {
    const data = await getTemplateReportAggregationData({
      siteId,
      startDate: getLocalizedDate(startDate).toISOString(),
      templateReportId,
      endDate: getLocalizedDate(endDate).toISOString(),
    })
    return data
  }

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true)
        const data = await fetchFunction()
        setReports(data.data)
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [startDate, endDate, siteId, templateReportId])

  if (!templateReportId) {
    history.push({
      pathname: `/`,
    })
    return null
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-lg">Aggregation Report</div>
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => setShowDateRangeFormModal(true)}>
          <MdOutlineCalendarToday className="text-gray-500" />
          <div className="flex items-center gap-2">
            <span>
              {dayjs(getLocalizedDate(startDate)).format('MMM DD, YYYY')} {' - '}
              {dayjs(getLocalizedDate(endDate)).format('MMM DD, YYYY')}
            </span>
          </div>
        </div>
      </div>

      {showDateRangeFormModal && 
        <Modal
          closeHandler={() => setShowDateRangeFormModal(false)}
          header="Select Date Range"
        >
          <DateRangeForm initStartDate={startDate} initEndDate={endDate} onConfirm={(start, end)=>{
            history.push({
              pathname: `/site/aggregation-report/${siteId}`,
              search: `?startDate=${start}&endDate=${end}&trId=${templateReportId}`,
            })
            setStartDate(start)
            setEndDate(end)
            setShowDateRangeFormModal(false)
          }} onCancel={()=>setShowDateRangeFormModal(false)} />
        </Modal>
      }

      {loading && <Loading overlay />}

      <div className="mt-4">
        {paginatedReports?.length > 0 && (
          <div className='space-y-10'>
            <Stack space="base">
                {paginatedReports?.map(
                  (report) =>
                    (  
                      <AggregationReportItem key={report.key} report={report} />
                    ),
                )}
            </Stack>

            <Pagination
              recordsPerPage={limit}
              skippedRecords={skippedRecords}
              onPageClick={setSkippedRecords}
              showDescription={false}
              totalRecords={reports.length}
            />
          </div>
        )}

        {!loading && !reports && (
          <div className="text-center mt-10 ">
            <p>No data available for the selected date range.</p>
          </div>
        )}
      </div>
    </div>
  )
}

ViewAggregationReport.defaultProps = {}

export { ViewAggregationReport }
